import React from "react";
import { Keyboard, MicOff, RotateCcw } from "lucide-react";

const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isIOS = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
const isAndroid = /Android/.test(ua);
const isIOSChrome = isIOS && /CriOS/.test(ua);

const steps = () => {
  if (isIOSChrome) return {
    device: "iPhone · Chrome",
    list: ["Open your iPhone Settings app", "Scroll down and tap Chrome", "Turn Microphone on", "Come back here and tap Try again"],
  };
  if (isIOS) return {
    device: "iPhone · Safari",
    list: ["Tap the aA (or ☰) button on the left of the web address", "Tap Website Settings", "Set Microphone to Allow", "Tap Done, then Try again below"],
    extra: "Still blocked? Open Settings → Apps → Safari → Microphone and choose Ask or Allow.",
  };
  if (isAndroid) return {
    device: "Android · Chrome",
    list: ["Tap the icon on the left of the web address", "Tap Permissions", "Set Microphone to Allow", "Come back and tap Try again"],
  };
  return {
    device: "Computer",
    list: ["Click the icon on the left of the web address", "Set Microphone to Allow", "Click Try again below (or refresh the page)"],
  };
};

// Shown whenever the browser refuses the mic: exact steps for this device, plus a way to keep going by typing.
export const MicHelp = ({ kind, onRetry, onType, gold = "#D7BB7A", cream = "#F1ECDD" }) => {
  const s = steps();
  const noMic = kind === "nomic";
  return (
    <div className="rounded-2xl p-5 text-left" style={{ background: "rgba(8,12,20,0.92)", border: `1px solid ${gold}55`, color: cream, fontFamily: "Manrope, sans-serif" }} data-testid="mic-help">
      <div className="flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.24em]" style={{ color: gold }}>
        <MicOff size={13} /> {noMic ? "No microphone found" : `Microphone is blocked · ${s.device}`}
      </div>
      <p className="mt-3 text-sm leading-relaxed" style={{ color: cream }}>
        {noMic ? "I can't find a microphone on this device. You can carry on by typing." : "To talk out loud, your browser needs permission to use the microphone. It takes a few seconds:"}
      </p>
      {!noMic && (
        <ol className="mt-3 space-y-1.5 text-sm" data-testid="mic-help-steps">
          {s.list.map((t, i) => (
            <li key={t} className="flex gap-3" style={{ color: cream }}><span className="font-bold" style={{ color: gold }}>{i + 1}</span><span>{t}</span></li>
          ))}
        </ol>
      )}
      {!noMic && s.extra && <p className="mt-3 text-xs" style={{ color: cream, opacity: 0.75 }}>{s.extra}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        {!noMic && (
          <button type="button" onClick={onRetry} data-testid="mic-help-retry"
            className="rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] font-bold inline-flex items-center gap-2 transition-transform duration-200 active:scale-95" style={{ background: gold, color: "#0b1430" }}>
            <RotateCcw size={13} /> Try again
          </button>
        )}
        {onType && <button type="button" onClick={onType} data-testid="mic-help-type"
          className="rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-2 transition-transform duration-200 active:scale-95" style={{ border: `1px solid ${gold}`, color: gold }}>
          <Keyboard size={13} /> Type instead
        </button>}
      </div>
    </div>
  );
};

const MIC_KEY = "soulumo_mic_ok";
// True once this device has allowed the mic (checked once, then remembered).
export const useMicReady = () => {
  const [ready, setReady] = React.useState(() => localStorage.getItem(MIC_KEY) === "1");
  React.useEffect(() => {
    if (ready) return;
    navigator.permissions?.query({ name: "microphone" })
      .then((p) => { if (p.state === "granted") { localStorage.setItem(MIC_KEY, "1"); setReady(true); } })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ready, () => { localStorage.setItem(MIC_KEY, "1"); setReady(true); }];
};

// First-time "say hello" check so mic permission is sorted before the conversation starts.
export const MicCheck = ({ name = "your guide", onReady, onType, gold = "#D7BB7A", cream = "#F1ECDD" }) => {
  const [phase, setPhase] = React.useState("ask");
  const [level, setLevel] = React.useState(0);
  const [fail, setFail] = React.useState("");
  const stop = React.useRef(() => {});
  React.useEffect(() => () => stop.current(), []);
  const start = async () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    ctx.resume?.();
    try {
      const st = await navigator.mediaDevices.getUserMedia({ audio: true });
      const an = ctx.createAnalyser(); an.fftSize = 512;
      ctx.createMediaStreamSource(st).connect(an);
      const buf = new Uint8Array(an.fftSize);
      let loud = 0, raf = 0;
      stop.current = () => { cancelAnimationFrame(raf); st.getTracks().forEach((t) => t.stop()); ctx.close().catch(() => {}); };
      setPhase("hello");
      const tick = () => {
        an.getByteTimeDomainData(buf);
        let s = 0; for (let i = 0; i < buf.length; i += 1) { const v = (buf[i] - 128) / 128; s += v * v; }
        const rms = Math.sqrt(s / buf.length);
        setLevel(Math.min(1, rms * 8));
        loud = rms > 0.035 ? loud + 1 : Math.max(0, loud - 1);
        if (loud > 10) { stop.current(); setPhase("heard"); setTimeout(onReady, 1300); return; }
        raf = requestAnimationFrame(tick);
      };
      tick();
    } catch (e) { ctx.close().catch(() => {}); setFail(e?.name === "NotFoundError" ? "nomic" : "blocked"); }
  };
  if (fail) return <MicHelp kind={fail} onRetry={() => { setFail(""); start(); }} onType={onType} gold={gold} cream={cream} />;
  return (
    <div className="rounded-2xl p-5 text-center" style={{ background: "rgba(8,12,20,0.85)", border: `1px solid ${gold}55`, color: cream, fontFamily: "Manrope, sans-serif" }} data-testid="mic-check">
      <p className="text-lg" style={{ fontFamily: '"Cormorant Garamond", serif', color: cream }} data-testid="mic-check-text">
        {phase === "ask" ? `Before we talk, let's make sure ${name} can hear you.`
          : phase === "hello" ? "Lovely. Now just say hello…" : "Perfect, I can hear you."}
      </p>
      {phase === "ask" && <p className="mt-2 text-xs" style={{ color: cream, opacity: 0.8 }}>When your phone asks, tap <b>Allow</b>.</p>}
      {phase === "hello" && (
        <div className="mt-4 mx-auto h-2 w-48 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.1)" }}>
          <div className="h-full rounded-full transition-[width] duration-100" style={{ width: `${Math.max(6, level * 100)}%`, background: gold }} data-testid="mic-check-level" />
        </div>
      )}
      {phase === "ask" && (
        <button type="button" onClick={start} data-testid="mic-check-allow"
          className="mt-4 rounded-full px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold transition-transform duration-200 active:scale-95" style={{ background: gold, color: "#0b1430" }}>
          Allow microphone
        </button>
      )}
      {phase !== "heard" && (
        <button type="button" onClick={phase === "hello" ? () => { stop.current(); onReady(); } : onType} data-testid="mic-check-skip" className="mt-3 block mx-auto text-xs underline" style={{ color: cream, opacity: 0.75 }}>
          {phase === "hello" ? "Skip the check" : "Type instead"}
        </button>
      )}
    </div>
  );
};
